import React from 'react';
import { X } from 'lucide-react';

interface PrivacyPolicyProps {
  onClose: () => void;
}

export function PrivacyPolicy({ onClose }: PrivacyPolicyProps) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <X size={24} />
        </button>

        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
          Privacy Policy & Data Protection Statement
        </h2>

        <div className="prose dark:prose-invert max-w-none">
          <h3>1. Introduction</h3>
          <p>
            This Privacy Policy explains how Daily Devotions ("we", "our", "us") collects, uses, and protects your personal data in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
          </p>

          <h3>2. Information We Collect</h3>
          <p>We collect and process the following information:</p>
          <ul>
            <li>Email address (for authentication)</li>
            <li>Reading progress and completed devotions</li>
            <li>Personal notes related to devotions</li>
            <li>Event information (titles, descriptions, dates, locations, and event types)</li>
            <li>Usage data (e.g., app interactions, preferences)</li>
          </ul>

          <h3>3. How We Use Your Information</h3>
          <p>We use your personal data for:</p>
          <ul>
            <li>Providing access to the Daily Devotions app</li>
            <li>Saving and managing your devotional progress</li>
            <li>Storing your personal notes and reflections</li>
            <li>Managing your calendar events and event details</li>
            <li>Improving the app experience</li>
            <li>Ensuring the security of your account</li>
          </ul>

          <h3>4. Legal Basis for Processing</h3>
          <p>We process your data based on:</p>
          <ul>
            <li>Your consent</li>
            <li>The performance of our contract with you</li>
            <li>Our legitimate interests in providing and improving the service</li>
          </ul>

          <h3>5. Data Storage and Security</h3>
          <p>
            Your data is stored securely in the UK/EEA using industry-standard encryption and security measures. We use Supabase for data storage, which maintains high security standards and UK GDPR compliance.
          </p>

          <h3>6. Your Rights</h3>
          <p>Under UK GDPR, you have the right to:</p>
          <ul>
            <li>Access your personal data</li>
            <li>Rectify inaccurate data</li>
            <li>Request erasure of your data</li>
            <li>Restrict or object to processing</li>
            <li>Data portability</li>
            <li>Withdraw consent</li>
          </ul>

          <h3>7. Data Retention</h3>
          <p>
            We retain your personal data only for as long as necessary to provide you with our services and as required by law. You can request deletion of your account and associated data at any time.
          </p>

          <h3>8. Location Data</h3>
          <p>
            If you choose to add location information to events, we store this data to provide mapping functionality. You can use the app without providing location data, and you can delete events with location data at any time.
          </p>

          <h3>9. Events and Calendar Information</h3>
          <p>
            When you create events in the app, we store the event details including title, description, date, location, and event type. You can choose to make events private (visible only to you) or public (visible to all users). You maintain full control over your events and can edit or delete them at any time.
          </p>

          <h3>10. Contact Information</h3>
          <p>
            For any questions about this Privacy Policy or to exercise your rights, please contact us at privacy@africanrevival.online
          </p>

          <h3>11. Updates to This Policy</h3>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any significant changes through the app or via email.
          </p>
        </div>
      </div>
    </div>
  );
}